import { planOf, trialEndingSoon, type PlanState } from '@shh/shared';
import { Button, Sheet, Text, cn } from '@shh/ui';
import { Link, useNavigate } from 'react-router';
import { useFamily } from '../data/family';
import { plusText } from '../i18n/plus';

/** What the family has right now (trial included) – recomputed on every render, cheap. */
export function usePlan(): PlanState {
  const { household } = useFamily();
  return planOf({ plan: household?.plan, createdAt: household?.createdAt });
}

/** The small, calm "Plus" mark next to a Plus function – never a lock (Mike, 14.10.). */
export function PlusBadge({ className }: { className?: string }) {
  return (
    <span
      className={cn(
        'rounded-chip bg-accent-secondary-soft px-inset-xs text-xs font-bold text-accent-secondary',
        className,
      )}
    >
      {plusText.badge}
    </span>
  );
}

/** A limit or a Plus function was reached: say what stays, show the way, no pressure. */
export function PlusSheet({
  isOpen,
  onClose,
  title,
  text,
  tip,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  text: string;
  tip?: string;
}) {
  const navigate = useNavigate();
  return (
    <Sheet isOpen={isOpen} onOpenChange={(o) => !o && onClose()} title={title}>
      <div className="flex flex-col gap-gap-md">
        <PlusBadge className="self-start" />
        <Text>{text}</Text>
        <Button
          onPress={() => {
            onClose();
            void navigate('/einstellungen#plus');
          }}
        >
          {plusText.more}
        </Button>
        <Button variant="quiet" onPress={onClose}>
          {plusText.notNow}
        </Button>
        {tip && (
          <Text size="sm" tone="secondary">
            {tip}
          </Text>
        )}
      </div>
    </Sheet>
  );
}

/** "Euer Probemonat endet in 3 Tagen" – parents only, only in the last days of the trial. */
export function TrialEndingNote() {
  const plan = usePlan();
  if (!trialEndingSoon(plan)) return null;
  return (
    <Link
      to="/einstellungen#plus"
      className="self-start rounded-card bg-accent-secondary-soft px-inset-sm py-inset-xs text-sm outline-none focus-visible:outline-3 focus-visible:outline-border-focus"
    >
      {plusText.trialEnding(plan.trialDaysLeft ?? 1)}
    </Link>
  );
}
